import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
} from 'recharts';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import type { ToolDashboardEntry } from '../../utils/dashboard';
import { metricRegistry } from '../../registry/metrics';
import { TONE_COLOR, finalStateTone } from '../../utils/stateTone';

type Axis = 'vc' | 'fz' | 'ap';

type Props = {
  entries: ToolDashboardEntry[];
  axis: Axis;
  noteKey: string;
};

type DotProps = {
  cx?: number;
  cy?: number;
  payload?: ScatterPoint;
};

function getAxisValue(entry: ToolDashboardEntry, axis: Axis): number | null {
  if (axis === 'vc') return entry.avgVc;
  if (axis === 'fz') return entry.avgFz;
  return entry.avgAp;
}

type ScatterPoint = {
  x: number;
  y: number;
  toolId: string;
  /** Final tool state, drives the dot colour. */
  state: string;
};

function computeSweetSpot(valid: { x: number; y: number }[]): { x1: number; x2: number } | null {
  if (valid.length < 2) return null;
  const sorted = [...valid].sort((a, b) => b.y - a.y);
  const topHalf = sorted.slice(0, Math.ceil(sorted.length / 2));
  const xs = topHalf.map((p) => p.x);
  return { x1: Math.min(...xs), x2: Math.max(...xs) };
}

export function ScatterPanel({ entries, axis, noteKey }: Props) {
  const { t } = useTranslation();

  const validEntries = entries.filter((e) => getAxisValue(e, axis) !== null);

  if (!validEntries.length) {
    return (
      <p className="text-sm text-muted-foreground">{t('analytics.dashboard.scatter.nodata')}</p>
    );
  }

  // One flat series coloured per point: with one <Scatter> per state (each holding its
  // own data) the Tooltip payload mixes in unrelated items and payload[0] is not the
  // hovered point (recharts#6075, Recharts 3.1.x).
  const points: ScatterPoint[] = validEntries.map((entry) => ({
    x: getAxisValue(entry, axis) as number,
    y: entry.totalPieces,
    toolId: entry.toolId,
    state: entry.finalState,
  }));
  const sweetSpot = computeSweetSpot(points);

  // Axis titles: the cutting parameter with its unit (from the metric registry) and the piece count.
  const unit = metricRegistry.find((m) => m.key === axis)?.unit;
  const xAxisTitle = `${t(`analytics.dashboard.scatter.axisLabels.${axis}`)}${unit ? ` [${unit}]` : ''}`;
  const yAxisTitle = t('analytics.dashboard.scatter.pieces');
  const axisTitleStyle = { fontSize: 11, fill: 'var(--muted-foreground)' };

  return (
    <div className="flex flex-col gap-2">
      <ResponsiveContainer width="100%" height={220}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 22, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="x"
            type="number"
            name={axis}
            tick={{ fontSize: 11 }}
            tickLine={false}
            label={{ value: xAxisTitle, position: 'insideBottom', offset: -14, ...axisTitleStyle }}
          />
          <YAxis
            dataKey="y"
            type="number"
            name="pieces"
            tick={{ fontSize: 11 }}
            tickLine={false}
            label={{
              value: yAxisTitle,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              ...axisTitleStyle,
            }}
          />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ payload }) => {
              if (!payload?.length) return null;
              const d = payload[0].payload as ScatterPoint;
              return (
                <div className="bg-card border border-border rounded px-2 py-1 text-xs">
                  <Link
                    to={`/traceability/tools/${encodeURIComponent(d.toolId)}`}
                    className="font-mono text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {d.toolId}
                  </Link>
                  <div>
                    {t(`analytics.dashboard.scatter.axisLabels.${axis}`)}: {d.x}
                  </div>
                  <div>
                    {t('analytics.dashboard.scatter.pieces')}: {d.y}
                  </div>
                </div>
              );
            }}
          />
          {sweetSpot && (
            <ReferenceArea
              x1={sweetSpot.x1}
              x2={sweetSpot.x2}
              fill="var(--primary)"
              fillOpacity={0.06}
            />
          )}
          <Scatter
            data={points}
            isAnimationActive={false}
            // Recharts 3 types ScatterCustomizedShape as (props: unknown) => Element,
            // so narrow to the dot coordinates and payload we actually consume.
            shape={(props: unknown) => {
              const { cx, cy, payload } = props as DotProps;
              const fill = TONE_COLOR[finalStateTone(payload?.state ?? '')];
              return <circle cx={cx ?? 0} cy={cy ?? 0} r={5} fill={fill} opacity={0.82} />;
            }}
          />
        </ScatterChart>
      </ResponsiveContainer>
      <p className="text-xs text-muted-foreground italic">{t(noteKey)}</p>
    </div>
  );
}
